import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { looksLikePhone } from "@/lib/phone-mask";
import { ilikeOrValue, phoneIlikeOrValue } from "@/lib/postgrestOr";
import { useCurrentOrgId } from '../auth';

const SEARCHABLE_CUSTOM_FIELD_TYPES = ["text", "textarea", "select"] as const;

// Hotfix Sprint 0: tokenização + stopwords para evitar timeouts em termos
// com muitas palavras decorativas (ex: "X | New Deal" onde "new deal" aparece
// em milhares de títulos). Será removido quando o Meilisearch entrar.
const STOPWORDS = new Set([
  // Preposições/artigos PT
  'de', 'da', 'do', 'das', 'dos', 'a', 'o', 'e', 'em', 'para', 'com', 'sem',
  // Palavras decorativas comuns do domínio
  'new', 'deal', 'lead', 'untitled', 'novo', 'nova',
]);

function sanitizeSearchTerm(raw: string): string {
  const collapsed = raw.trim().replace(/\s+/g, ' ');
  if (!collapsed) return '';
  const tokens = collapsed.toLowerCase().split(/[\s|]+/).filter(Boolean);
  const kept = tokens.filter((t) => t.length >= 3 && !STOPWORDS.has(t)).slice(0, 4);
  return kept.length > 0 ? kept.join(' ') : collapsed;
}

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'lead' | 'company' | 'deal' | 'activity' | 'sequence';
  href: string;
  status?: string;
  amount?: number; // valor do deal (R$), exibido em destaque na linha
  matchedField?: string;
  matchedValue?: string;
  // Highlight do Meilisearch (string com sentinelas, ver Highlighted.tsx).
  // Opcionais: o fallback RPC/legacy não os preenche.
  highlightedTitle?: string;
  matchedSnippet?: string;
  matchedFieldLabel?: string;
}

type RpcRow = {
  id: string;
  type: SearchResult['type'];
  title: string;
  subtitle: string | null;
  status: string | null;
  matched_field: string | null;
  matched_value: string | null;
  amount: number | null;
  rank: number;
  exact: boolean;
  group_priority: number;
};

const HREF_BY_TYPE: Record<SearchResult['type'], (id: string) => string> = {
  lead: (id) => `/leads/${id}`,
  deal: (id) => `/deals/${id}`,
  company: (id) => `/companies/${id}`,
  activity: (id) => `/activities?highlight=${id}`,
  sequence: (id) => `/sequences/${id}`,
};

function mapRpcRow(row: RpcRow): SearchResult {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    subtitle: row.subtitle ?? undefined,
    status: row.status ?? undefined,
    amount: row.type === 'deal' && row.amount != null ? row.amount : undefined,
    matchedField: row.matched_field ?? undefined,
    matchedValue: row.matched_value ?? undefined,
    href: HREF_BY_TYPE[row.type](row.id),
  };
}

// Fallback de busca em custom fields: a RPC v1 não inclui custom_data.
// Mantemos as queries PostgREST originais como suplemento e mesclamos os ids
// de volta com o resultado da RPC (dedup por type+id).
async function searchCustomFields(
  orgId: string,
  rawQuery: string,
): Promise<SearchResult[]> {
  const searchTerm = ilikeOrValue(rawQuery);

  const { data: customFieldsRows } = await supabase
    .from("organization_custom_fields")
    .select("field_name, field_type, page_type")
    .eq("org_id", orgId)
    .eq("is_active", true);

  if (!customFieldsRows || customFieldsRows.length === 0) return [];

  const byPage = customFieldsRows.reduce(
    (acc, row) => {
      const page = row.page_type as 'leads' | 'companies' | 'deals' | 'activities';
      if (!SEARCHABLE_CUSTOM_FIELD_TYPES.includes(row.field_type as (typeof SEARCHABLE_CUSTOM_FIELD_TYPES)[number])) {
        return acc;
      }
      if (!acc[page]) acc[page] = [];
      acc[page].push(row.field_name);
      return acc;
    },
    {} as Record<'leads' | 'companies' | 'deals' | 'activities', string[]>,
  );

  const buildOr = (fields: string[] = []) =>
    fields.map((f) => `custom_data->>${f}.ilike.${searchTerm}`).join(',');

  const tasks: Promise<SearchResult[]>[] = [];

  if (byPage.leads?.length) {
    tasks.push(
      supabase
        .from('leads')
        .select('id, name, email, phone, companies(name)')
        .eq('org_id', orgId)
        .or(buildOr(byPage.leads))
        .limit(5)
        .then(({ data }) =>
          (data ?? []).map((l: any) => ({
            id: l.id,
            type: 'lead' as const,
            title: l.name,
            subtitle: l.companies?.name || l.email || l.phone || undefined,
            matchedField: 'Campo personalizado',
            matchedValue: undefined,
            href: HREF_BY_TYPE.lead(l.id),
          })),
        ),
    );
  }

  if (byPage.companies?.length) {
    tasks.push(
      supabase
        .from('companies')
        .select('id, name, industry, email, phone, status')
        .eq('org_id', orgId)
        .or(buildOr(byPage.companies))
        .limit(5)
        .then(({ data }) =>
          (data ?? []).map((c: any) => ({
            id: c.id,
            type: 'company' as const,
            title: c.name,
            subtitle: c.industry || c.email || c.phone || 'Empresa',
            status: c.status ?? undefined,
            matchedField: 'Campo personalizado',
            href: HREF_BY_TYPE.company(c.id),
          })),
        ),
    );
  }

  if (byPage.deals?.length) {
    tasks.push(
      supabase
        .from('deals')
        .select('id, title, amount, status, companies(name)')
        .eq('org_id', orgId)
        .neq('status', 'deleted') // deals excluídos não aparecem na busca
        .or(buildOr(byPage.deals))
        .limit(5)
        .then(({ data }) =>
          (data ?? []).map((d: any) => ({
            id: d.id,
            type: 'deal' as const,
            title: d.title,
            subtitle: d.companies?.name || undefined,
            status: d.status ?? undefined,
            amount: d.amount != null ? Number(d.amount) : undefined,
            matchedField: 'Campo personalizado',
            href: HREF_BY_TYPE.deal(d.id),
          })),
        ),
    );
  }

  if (byPage.activities?.length) {
    tasks.push(
      supabase
        .from('activities')
        .select('id, title, type, status, leads(name), companies(name)')
        .eq('org_id', orgId)
        .or(buildOr(byPage.activities))
        .limit(5)
        .then(({ data }) =>
          (data ?? []).map((a: any) => ({
            id: a.id,
            type: 'activity' as const,
            title: a.title,
            subtitle: a.leads?.name || a.companies?.name || a.type,
            status: a.status ?? undefined,
            matchedField: 'Campo personalizado',
            href: HREF_BY_TYPE.activity(a.id),
          })),
        ),
    );
  }

  const groups = await Promise.all(tasks);
  return groups.flat();
}

// Fallback ILIKE (sem unaccent/fuzzy) — usado enquanto a migration global_search
// não está aplicada no Supabase. Replica a busca antiga, incluindo o merge de
// deals via lead_id encontrado por telefone OU email (spec 1 em modo degradado).
async function legacyGlobalSearch(orgId: string, rawQuery: string): Promise<SearchResult[]> {
  const searchTerm = ilikeOrValue(rawQuery);
  const phoneDigits = rawQuery.replace(/\D/g, '');
  // Mesmo critério usado para montar o filtro de telefone: se o termo tem letras, a busca
  // não foi por telefone e o match abaixo não deve ser atribuído ao campo phone.
  const isLikelyPhone = looksLikePhone(rawQuery);
  const phoneSearchTerm = phoneIlikeOrValue(rawQuery);

  const [leadsRes, companiesRes, dealsRes, activitiesRes, sequencesRes] = await Promise.all([
    supabase
      .from('leads')
      .select('id, name, email, phone, status, companies(name)')
      .eq('org_id', orgId)
      .or(`name.ilike.${searchTerm},email.ilike.${searchTerm},phone.ilike.${phoneSearchTerm}`)
      .limit(5),
    supabase
      .from('companies')
      .select('id, name, industry, email, phone, status')
      .eq('org_id', orgId)
      .or(`name.ilike.${searchTerm},industry.ilike.${searchTerm},email.ilike.${searchTerm},phone.ilike.${phoneSearchTerm}`)
      .limit(5),
    supabase
      .from('deals')
      .select('id, title, status, amount, companies(name)')
      .eq('org_id', orgId)
      .neq('status', 'deleted') // deals excluídos não aparecem na busca
      // Busca só por title: description é HTML gigante (TOAST) e o recheck do
      // índice trigram destoasta tudo, causando timeout em orgs grandes.
      .or(`title.ilike.${searchTerm}`)
      .limit(5),
    supabase
      .from('activities')
      .select('id, title, type, status, leads(name), companies(name)')
      .eq('org_id', orgId)
      .or(`title.ilike.${searchTerm}`) // idem deals: sem description (TOAST caro)
      .limit(5),
    supabase
      .from('sequences')
      .select('id, name, description, is_active')
      .eq('org_id', orgId)
      .or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`)
      .limit(5),
  ]);

  // Coletar lead_ids cujo match foi por email/phone para puxar deals vinculados
  const lowerTerm = rawQuery.toLowerCase();
  const matchedLeadIds: string[] = [];
  const leadMatchedField: Record<string, { field: string; value: string | null }> = {};
  (leadsRes.data ?? []).forEach((l: any) => {
    const byEmail = l.email && l.email.toLowerCase().includes(lowerTerm);
    const byPhone = isLikelyPhone && l.phone && l.phone.replace(/\D/g, '').includes(phoneDigits);
    if (byEmail || byPhone) {
      matchedLeadIds.push(l.id);
      leadMatchedField[l.id] = byEmail
        ? { field: 'Email do lead', value: l.email }
        : { field: 'Telefone do lead', value: l.phone };
    }
  });

  let dealsByLead: any[] = [];
  if (matchedLeadIds.length > 0) {
    const { data } = await supabase
      .from('deals')
      .select('id, title, status, amount, lead_id, companies(name)')
      .eq('org_id', orgId)
      .neq('status', 'deleted') // deals excluídos não aparecem na busca
      .in('lead_id', matchedLeadIds)
      .limit(5);
    dealsByLead = data ?? [];
  }

  const results: SearchResult[] = [];

  (leadsRes.data ?? []).forEach((l: any) => {
    const matched = leadMatchedField[l.id];
    results.push({
      id: l.id,
      type: 'lead',
      title: l.name,
      subtitle: l.companies?.name || l.email || l.phone || undefined,
      status: l.status ?? undefined,
      matchedField: matched?.field,
      matchedValue: matched?.value ?? undefined,
      href: HREF_BY_TYPE.lead(l.id),
    });
  });

  // Deals: priorizar match por título (até 5), depois lead-linked (até preencher 5)
  const titleDealIds = new Set((dealsRes.data ?? []).map((d: any) => d.id));
  const titleDeals = (dealsRes.data ?? []).slice(0, 5);
  const linkedOnly = dealsByLead.filter((d: any) => !titleDealIds.has(d.id));
  const remainingSlots = Math.max(0, 5 - titleDeals.length);
  const finalDeals = [...titleDeals, ...linkedOnly.slice(0, remainingSlots)];

  finalDeals.forEach((d: any) => {
    const isLinked = !titleDealIds.has(d.id);
    const linkedMatch = isLinked ? leadMatchedField[d.lead_id] : undefined;
    results.push({
      id: d.id,
      type: 'deal',
      title: d.title,
      subtitle: d.companies?.name || undefined,
      status: d.status ?? undefined,
      amount: d.amount != null ? Number(d.amount) : undefined,
      matchedField: linkedMatch?.field,
      matchedValue: linkedMatch?.value ?? undefined,
      href: HREF_BY_TYPE.deal(d.id),
    });
  });

  (companiesRes.data ?? []).forEach((c: any) => {
    results.push({
      id: c.id,
      type: 'company',
      title: c.name,
      subtitle: c.industry || c.email || c.phone || 'Empresa',
      status: c.status ?? undefined,
      href: HREF_BY_TYPE.company(c.id),
    });
  });

  (activitiesRes.data ?? []).forEach((a: any) => {
    results.push({
      id: a.id,
      type: 'activity',
      title: a.title,
      subtitle: a.leads?.name || a.companies?.name || a.type,
      status: a.status ?? undefined,
      href: HREF_BY_TYPE.activity(a.id),
    });
  });

  (sequencesRes.data ?? []).forEach((s: any) => {
    results.push({
      id: s.id,
      type: 'sequence',
      title: s.name,
      subtitle: s.description || 'Cadência',
      status: s.is_active ? 'active' : 'inactive',
      href: HREF_BY_TYPE.sequence(s.id),
    });
  });

  return results;
}

// PostgREST error code for "function not found"
const RPC_NOT_FOUND_CODES = new Set(['PGRST202', '42883']);

// Feature flag: quando VITE_USE_MEILISEARCH=true, a busca é roteada para a
// Edge Function `meilisearch-search` (typo-tolerance + ranking decentes,
// ver `infra/meilisearch/`). Caso contrário, mantém o caminho atual via RPC
// `global_search` + fallback ILIKE.
const USE_MEILISEARCH = import.meta.env.VITE_USE_MEILISEARCH === 'true';

async function meilisearchSearch(orgId: string, query: string): Promise<SearchResult[]> {
  const { data, error } = await supabase.functions.invoke<{ results: SearchResult[] }>(
    'meilisearch-search',
    { body: { q: query, org_id: orgId, limit: 5 } },
  );
  if (error) {
    console.error('[useGlobalSearch] meilisearch-search error:', error);
    return [];
  }
  return data?.results ?? [];
}

// Merge + dedup: prioriza `primary` (já vem ranqueado da RPC/meili) e adiciona
// os itens de `extra` (custom fields) que ainda não apareceram, até 5 por tipo.
function mergeResults(primary: SearchResult[], extra: SearchResult[]): SearchResult[] {
  if (extra.length === 0) return primary;
  const seen = new Set(primary.map((r) => `${r.type}:${r.id}`));
  const countByType: Record<SearchResult['type'], number> = {
    lead: 0, deal: 0, company: 0, activity: 0, sequence: 0,
  };
  primary.forEach((r) => { countByType[r.type] += 1; });

  const merged: SearchResult[] = [...primary];
  for (const r of extra) {
    const key = `${r.type}:${r.id}`;
    if (seen.has(key)) continue;
    if (countByType[r.type] >= 5) continue;
    seen.add(key);
    countByType[r.type] += 1;
    merged.push(r);
  }
  return merged;
}

// Busca "primária": rápida (phone_search OU global_search com fallback legacy).
// NÃO inclui custom fields — esses vêm numa query separada que não bloqueia.
async function primarySearch(orgId: string, query: string): Promise<SearchResult[]> {
  // Caminho Meilisearch: sem sanitize (deixa Meili lidar com typos/stopwords).
  if (USE_MEILISEARCH) {
    return meilisearchSearch(orgId, query.trim());
  }

  // Telefone: RPC dedicada e rápida (evita o timeout da global_search).
  if (looksLikePhone(query)) {
    const { data, error } = await supabase.rpc('phone_search', {
      p_org_id: orgId,
      p_query: query.trim(),
      p_limit: 5,
    });
    if (!error) return (data ?? []).map((r: RpcRow) => mapRpcRow(r));
    console.error('[useGlobalSearch] phone_search RPC error; usando fluxo padrão:', error);
  }

  const rawQuery = sanitizeSearchTerm(query);
  if (!rawQuery) return [];

  const rpcRes = await supabase.rpc('global_search', {
    p_org_id: orgId,
    p_query: rawQuery,
    p_limit: 5,
  });

  // Fallback: qualquer erro da RPC (migration ausente OU timeout/500 em orgs
  // grandes) cai para a busca legacy via PostgREST, para não deixar resultados
  // sumirem silenciosamente (ex.: deals que existem na pipeline mas somem da
  // busca quando a global_search estoura o statement_timeout).
  if (rpcRes.error) {
    const code = (rpcRes.error as any).code;
    console.warn(
      RPC_NOT_FOUND_CODES.has(code)
        ? '[useGlobalSearch] RPC global_search indisponível; usando fallback ILIKE.'
        : `[useGlobalSearch] RPC global_search falhou (${code ?? 'sem código'}); usando fallback ILIKE.`
    );
    return legacyGlobalSearch(orgId, rawQuery);
  }

  return (rpcRes.data ?? []).map((r: RpcRow) => mapRpcRow(r));
}

export const useGlobalSearch = (query: string) => {
  const { orgId } = useCurrentOrgId();
  const enabledBase = !!orgId && !!query.trim();

  const mainQuery = useQuery({
    queryKey: ["global-search", query, orgId, USE_MEILISEARCH ? 'meili' : 'pg'],
    queryFn: async (): Promise<SearchResult[]> => {
      try {
        return await primarySearch(orgId!, query);
      } catch (error) {
        console.error('Search error:', error);
        return [];
      }
    },
    enabled: enabledBase,
    staleTime: USE_MEILISEARCH ? 5_000 : 30_000,
    gcTime: 60_000,
  });

  // Custom fields: query SEPARADA que não bloqueia os resultados da RPC (que
  // são rápidos). São scans não indexados em custom_data (JSONB), então rodavam
  // em ~centenas de ms a segundos em orgs grandes e seguravam a UI inteira.
  // Agora mesclam quando resolvem. Só no caminho pg e fora de telefone.
  const rawForCustom = USE_MEILISEARCH ? '' : sanitizeSearchTerm(query);
  const customEnabled = enabledBase && !USE_MEILISEARCH && !looksLikePhone(query) && !!rawForCustom;

  const customQuery = useQuery({
    queryKey: ["global-search-custom", query, orgId],
    queryFn: async (): Promise<SearchResult[]> => {
      try {
        return await searchCustomFields(orgId!, rawForCustom);
      } catch (e) {
        console.error('Custom fields search error:', e);
        return [];
      }
    },
    enabled: customEnabled,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const data = useMemo(
    () => mergeResults(mainQuery.data ?? [], customQuery.data ?? []),
    [mainQuery.data, customQuery.data],
  );

  // Mantém o contrato do hook ({ data, isLoading, ... }). O isLoading reflete
  // apenas a query principal (rápida) — o custom mescla depois sem travar a UI.
  return { ...mainQuery, data };
};
